import type { Translation } from './en';

// French typography uses a non-breaking space before : ; ! ? and inside « ».
const fr: Translation = {
  app: {
    name: 'Dishly',
  },
  common: {
    cancel: 'Annuler',
    close: 'Fermer',
    save: 'Enregistrer',
    loading: 'Chargement…',
    retry: 'Réessayer',
    create: 'Créer',
    undo: 'Annuler',
    more: 'Plus d’options',
  },
  nav: {
    home: 'Accueil Dishly',
    add: 'Ajouter',
    addRecipe: 'Ajouter une recette',
    shoppingList: 'Liste de courses',
    lists: 'Mes listes',
    myRecipes: 'Mes recettes',
    account: 'Compte',
    logIn: 'Connexion / Inscription',
    logOut: 'Se déconnecter',
    themeDark: 'Passer au thème sombre',
    themeLight: 'Passer au thème clair',
    language: 'Langue',
    userMenu: 'Menu du compte',
    offline: 'Hors ligne',
    offlineHint: 'Vous êtes hors ligne. Les recettes téléchargées et vos listes fonctionnent toujours.',
  },
  home: {
    title: 'Recettes',
    count_one: '{{count}} recette',
    count_other: '{{count}} recettes',
    tags: 'Étiquettes',
    showAll: 'Tout afficher',
    sortLabel: 'Trier par',
    sort: {
      newest: 'Plus récentes',
      rating: 'Mieux notées',
      quickest: 'Plus rapides',
      name: 'A–Z',
    },
    viewGrid: 'Vue en grille',
    viewList: 'Vue en liste',
    empty: 'Aucune recette pour l’instant.',
    emptyHint: 'Soyez la première personne à en partager une !',
    noMatches: 'Aucune recette ne correspond à toutes ces étiquettes.',
    noMatchesHint: 'Essayez de retirer une étiquette.',
    offlineEmpty: 'Vous êtes hors ligne et aucune recette n’a encore été téléchargée. Connectez-vous une fois et elles seront disponibles hors ligne.',
    end: 'C’est tout !',
    backToTop: 'Revenir en haut',
  },
  tags: {
    breakfast: 'Petit-déjeuner',
    brunch: 'Brunch',
    lunch: 'Déjeuner',
    dinner: 'Dîner',
    vegetarian: 'Végétarien',
    vegan: 'Végan',
    'gluten-free': 'Sans gluten',
  },
  rating: {
    none: 'Pas encore de note',
    label_one: 'Noté {{rating}} sur 5 par {{count}} personne',
    label_other: 'Noté {{rating}} sur 5 par {{count}} personnes',
    rateStars_one: 'Donner {{count}} étoile',
    rateStars_other: 'Donner {{count}} étoiles',
  },
  cart: {
    add: 'Ajouter à la liste de courses',
    remove: 'Retirer de la liste de courses',
    added: 'Ajoutée à votre liste de courses',
    removed: 'Retirée de votre liste de courses',
    view: 'Voir',
  },
  badge: {
    draft: 'Brouillon',
    private: 'Privée',
  },
  recipe: {
    by: 'par {{name}}',
    servings_one: '{{count}} portion',
    servings_other: '{{count}} portions',
    fewerServings: 'Moins de portions',
    moreServings: 'Plus de portions',
    units: 'Unités',
    metric: 'Métrique',
    imperial: 'Impérial',
    ingredients: 'Ingrédients',
    noIngredients: 'Aucun ingrédient.',
    method: 'Préparation',
    startCooking: 'Commencer à cuisiner',
    continueCooking: 'Reprendre à l’étape {{step}}',
    stepOf: 'Étape {{current}} sur {{total}}',
    next: 'Étape suivante',
    finish: 'Terminer',
    returnToCurrent: 'Revenir à l’étape en cours',
    progress: 'Progression dans la recette',
    goToStep: 'Aller à l’étape {{step}}',
    stepDone: 'Étape terminée',
    startOver: 'Recommencer',
    finishedTitle: 'Bon appétit !',
    finishedBody: 'Alors, c’était comment ?',
    rateThis: 'Notez cette recette',
    yourRating: 'Votre note',
    ownRecipe: 'C’est votre recette, vous ne pouvez donc pas la noter.',
    reviews: 'Avis',
    noReviews: 'Pas encore de commentaires.',
    loadMore: 'Afficher plus',
    favourite: 'Ajouter aux favoris',
    unfavourite: 'Retirer des favoris',
    favourited: 'Ajoutée aux favoris',
    unfavourited: 'Retirée des favoris',
    saveToList: 'Enregistrer dans une liste',
    newListPlaceholder: 'Nom de la nouvelle liste',
    edit: 'Modifier',
    delete: 'Supprimer',
    deleteTitle: 'Supprimer cette recette ?',
    deleteBody: 'Elle sera supprimée pour tout le monde. Cette action est irréversible.',
    deleted: 'Recette supprimée',
    draftNotice: 'Ceci est un brouillon. Vous seul·e pouvez le voir.',
    privateNotice: 'Cette recette est privée. Vous seul·e pouvez la voir.',
    notFound: 'Recette introuvable',
    notFoundBody: 'Elle a peut-être été supprimée ou rendue privée.',
    notDownloaded: 'Cette recette n’a pas encore été téléchargée. Connectez-vous à Internet pour l’ouvrir ; elle fonctionnera ensuite hors ligne.',
    addThese: 'Ajoutez :',
    notes: 'Notes',
    photo: 'Photo {{n}} sur {{total}}',
    startTimer: 'Lancer un minuteur de {{duration}}',
    timerRunning: 'Minuteur en cours',
  },
  steps: {
    add_ingredients: 'Ajouter des ingrédients',
    cook: 'Cuire',
    prep: 'Préparer',
  },
  heat: {
    low: 'Feu doux',
    medium_low: 'Feu moyen-doux',
    medium: 'Feu moyen',
    medium_high: 'Feu moyen-vif',
    high: 'Feu vif',
  },
  method: {
    pan: 'Poêle',
    pot: 'Casserole',
    oven: 'Four',
    grill: 'Gril',
    bbq: 'Barbecue',
    steamer: 'Cuit-vapeur',
    deep_fryer: 'Friteuse',
    air_fryer: 'Friteuse à air',
    microwave: 'Micro-ondes',
    slow_cooker: 'Mijoteuse',
    pressure_cooker: 'Autocuiseur',
    other: 'Autre',
  },
  review: {
    title: 'Merci pour votre note !',
    saved: 'Note enregistrée',
    commentLabel: 'Ajouter un commentaire (facultatif)',
    commentPlaceholder: 'Qu’en avez-vous pensé ? Des modifications ?',
    post: 'Publier le commentaire',
    skip: 'Non merci',
    editComment: 'Modifier le commentaire',
    remove: 'Retirer ma note',
    removed: 'Note retirée',
  },
  lists: {
    title: 'Mes listes',
    shopping: 'Liste de courses',
    favourites: 'Favoris',
    new: 'Nouvelle liste',
    namePlaceholder: 'p. ex. Dîners de la semaine',
    rename: 'Renommer la liste',
    delete: 'Supprimer la liste',
    deleteTitle: 'Supprimer « {{name}} » ?',
    deleteBody: 'Les recettes elles-mêmes ne sont pas supprimées.',
    empty: 'Cette liste est vide.',
    emptyShopping: 'Touchez le panier d’une recette pour ajouter ses ingrédients ici.',
    emptyOther: 'Enregistrez des recettes dans cette liste depuis leur page.',
    browse: 'Parcourir les recettes',
    recipes_one: '{{count}} recette',
    recipes_other: '{{count}} recettes',
    consolidate: 'Regrouper les ingrédients',
    consolidateHint: 'Une seule liste pour tout. Les recettes repliées ne sont pas incluses.',
    collapsedExcluded_one: '{{count}} recette repliée n’est pas incluse.',
    collapsedExcluded_other: '{{count}} recettes repliées ne sont pas incluses.',
    nothingToCombine: 'Dépliez une recette pour inclure ses ingrédients.',
    expand: 'Afficher les ingrédients',
    collapse: 'Masquer les ingrédients (pour plus tard)',
    remove: 'Retirer de la liste',
    removed: '« {{name}} » retirée',
    export: 'Exporter',
    copy: 'Copier dans le presse-papiers',
    copied: 'Copiée ! Collez-la dans Notes ou une autre app.',
    copyFailed: 'Impossible de copier la liste.',
    share: 'Partager…',
    unavailable: 'Cette recette n’est plus disponible.',
    uncheckAll: 'Tout décocher',
    progress: '{{done}} sur {{total}} cochés',
  },
  myRecipes: {
    title: 'Mes recettes',
    drafts: 'Brouillons',
    private: 'Privées',
    public: 'Publiques',
    emptyDrafts: 'Aucun brouillon. Les recettes que vous commencez sont enregistrées ici automatiquement.',
    emptyPrivate: 'Aucune recette privée.',
    emptyPublic: 'Vous n’avez encore publié aucune recette.',
    untitled: 'Recette sans titre',
    edited: 'Modifiée le {{date}}',
  },
  newRecipe: {
    title: 'Ajouter une recette',
    subtitle: 'Comment souhaitez-vous commencer ?',
    photoTitle: 'Photographier une recette papier',
    photoBody: 'Prenez en photo une fiche ou une page de livre de cuisine et l’IA la transforme en recette Dishly.',
    describeTitle: 'Décrire ou dicter',
    describeBody: 'Écrivez ou dites ce que vous avez, comme « poulet, pommes de terre, chou-fleur et épices habituelles », et l’IA crée une recette.',
    manualTitle: 'L’écrire vous-même',
    manualBody: 'Ajoutez votre recette étape par étape. Votre travail est enregistré au fur et à mesure.',
    comingSoon: 'Bientôt disponible',
    comingSoonBody: 'La création de recettes par IA n’est pas encore disponible. En attendant, vous pouvez écrire la recette vous-même.',
    choosePhoto: 'Prendre ou choisir une photo',
    describePlaceholder: 'J’ai du poulet, des pommes de terre, du chou-fleur et des épices habituelles…',
    generate: 'Créer la recette',
    writeManually: 'L’écrire moi-même',
    drafts: 'Reprendre un brouillon',
    addPage: 'Ajouter une page',
    photoHint: 'Jusqu’à {{count}} photos, par exemple les deux pages d’un livre de cuisine.',
    noteLabel: 'Quelque chose à ajouter ? (facultatif)',
    notePlaceholder: 'p. ex. « Pour 6 personnes » ou « Au four plutôt qu’au gril »',
    voiceHint: 'Ou appuyez sur le micro et dites-le.',
    working: 'Rédaction de votre recette…',
    workingPhotos: 'Lecture de vos photos…',
    linkTitle: 'Coller un lien',
    linkBody: 'Une recette trouvée sur un site ? Collez le lien et l’IA l’importe dans Dishly, photo comprise.',
    linkLabel: 'Lien vers la recette',
    linkHint: 'Fonctionne avec la plupart des sites de recettes. Le site est cité dans la recette, et vous pouvez tout vérifier avant de la publier.',
    workingLink: 'Lecture de la page…',
    workingHint: 'Cela peut prendre jusqu’à une minute. Gardez cette page ouverte.',
  },
  save: {
    button: 'Enregistrer dans Dishly',
    title: 'Enregistrer dans Dishly',
    dragHint: 'Faites glisser ce bouton dans votre barre de favoris, puis cliquez dessus sur n’importe quelle page de recette.',
    tip: 'Enregistrez des recettes directement depuis votre navigateur : faites glisser ce bouton dans votre barre de favoris, puis cliquez dessus sur une page de recette.',
    setUp: 'Faites glisser ce bouton dans votre barre de favoris. Ensuite, sur n’importe quelle page de recette, cliquez dessus pour importer la recette dans Dishly.',
    howTo: 'Cette page s’ouvre quand vous cliquez sur « Enregistrer dans Dishly » dans votre barre de favoris, sur une page de recette. Sur un téléphone, collez plutôt le lien de la recette ou faites des captures d’écran.',
    popupBlocked: 'Autorisez les fenêtres pop-up sur ce site pour enregistrer la recette dans Dishly.',
    waiting: 'Récupération de la page de la recette…',
    nothing: 'Rien n’est arrivé de la page de la recette',
    nothingHint: 'Revenez à la page de la recette et cliquez à nouveau sur « Enregistrer dans Dishly ».',
    confirm: 'Importer cette recette de {{site}} dans Dishly ? L’IA la rédige en brouillon pour que vous la vérifiiez.',
    import: 'Importer la recette',
    blocked: '{{site}} ne permet pas aux applications de lire ses recettes.',
    blockedDesktop: 'Vous pouvez quand même l’enregistrer depuis votre navigateur : faites glisser le bouton ci-dessous dans votre barre de favoris, ouvrez la recette et cliquez sur le favori. Ou faites des captures d’écran et utilisez « Photographier une recette papier ».',
    blockedMobile: 'Faites des captures d’écran de la recette (jusqu’à 4) et utilisez « Photographier une recette papier ».',
  },
  voice: {
    start: 'Enregistrer votre voix',
    stop: 'Arrêter l’enregistrement',
    say: 'Dicter',
    transcribing: 'Transcription…',
    denied: 'L’accès au micro est bloqué. Autorisez-le dans les réglages du navigateur pour utiliser votre voix.',
    unavailable: 'Le micro n’a pas pu être activé.',
    nothingHeard: 'Nous n’avons pas compris. Veuillez réessayer.',
  },
  adapt: {
    open: 'Demander ou adapter',
    title: 'Poser une question ou adapter la recette',
    intro: 'Posez une question, comme « Mon four n’a pas de gril, que puis-je utiliser ? », ou dites ce qu’il faut changer, comme « Je n’ai pas de sauce poisson ». Les modifications ne concernent que vous.',
    placeholder: 'Posez une question ou dites ce qu’il faut changer…',
    submit: 'Envoyer',
    working: 'Réflexion…',
    you: 'Vous',
    changed: 'Recette adaptée',
    noChange: 'Rien n’a été modifié.',
    noticeTitle: 'Adaptée pour vous',
    noticeBody: 'Cette version n’est visible que par vous. La recette d’origine ne change pas.',
    changedStep: 'Modifiée',
    saveCopy: 'Enregistrer ma copie',
    copySaved: 'Enregistrée dans vos recettes',
    adaptedFrom: 'Adaptée de [{{name}}]({{path}}) par {{author}}.',
    apply: 'Appliquer à la recette',
    applyTitle: 'Mettre à jour votre recette avec ces modifications ?',
    applyBody: 'Toutes les personnes qui voient cette recette verront la version modifiée.',
    applied: 'Recette mise à jour',
    applyInvalid: 'Il manque quelque chose à la recette modifiée. Enregistrez-la comme copie ou modifiez la recette.',
    discard: 'Revenir à l’original',
  },
  editor: {
    newTitle: 'Nouvelle recette',
    editTitle: 'Modifier la recette',
    name: 'Nom de la recette',
    namePlaceholder: 'p. ex. Le crumble aux pommes de mamie',
    photos: 'Photos',
    photosHint: 'La première photo sert de couverture.',
    addPhotos: 'Ajouter des photos',
    uploading: 'Envoi…',
    cover: 'Couverture',
    makeCover: 'Utiliser comme couverture',
    removePhoto: 'Retirer la photo',
    uploadFailed: 'Échec de l’envoi : {{message}}',
    tooManyPhotos: 'Vous pouvez ajouter jusqu’à {{count}} photos.',
    description: 'À propos de cette recette',
    descriptionPlaceholder: 'Son histoire, des astuces, avec quoi la servir…',
    write: 'Écrire',
    preview: 'Aperçu',
    markdownHint: 'Markdown pris en charge : **gras**, *italique*, listes et liens.',
    nothingToPreview: 'Rien à afficher pour l’instant.',
    details: 'Détails',
    totalTime: 'Temps total (minutes)',
    stepTotal: 'Les étapes minutées totalisent {{duration}}.',
    useStepTotal: 'Utiliser cette valeur',
    servings: 'Portions',
    tags: 'Étiquettes',
    steps: 'Étapes',
    stepsHint: 'En cuisinant, on voit une étape à la fois.',
    noSteps: 'Aucune étape pour l’instant. Ajoutez la première ci-dessous.',
    addStep: 'Ajouter une étape',
    stepTypeHelp: {
      add_ingredients: 'Un ou plusieurs ingrédients avec leurs quantités',
      cook: 'Cuisson : faire revenir, enfourner, mijoter…',
      prep: 'Couper, mélanger, laisser reposer, chemiser une plaque…',
    },
    stepNumber: 'Étape {{n}}',
    stepType: 'Type d’étape',
    instruction: 'Instruction',
    instructionOptional: 'Instruction (facultative)',
    instructionPlaceholder: {
      add_ingredients: 'p. ex. Fouettez le tout dans un grand saladier',
      cook: 'p. ex. Faites revenir jusqu’à ce que ce soit tendre et doré',
      prep: 'p. ex. Coupez les tomates en dés',
    },
    ingredients: 'Ingrédients',
    addIngredient: 'Ajouter un ingrédient',
    ingredient: 'Ingrédient',
    ingredientPlaceholder: 'p. ex. huile d’olive',
    quantity: 'Quantité',
    quantityPlaceholder: 'p. ex. 1½',
    unit: 'Unité',
    unitNone: 'entier / sans unité',
    note: 'Note',
    notePlaceholder: 'p. ex. finement haché',
    removeIngredient: 'Retirer l’ingrédient',
    equipment: 'Ustensile',
    heat: 'Feu',
    notSpecified: 'Non précisé',
    temperature: 'Température (°{{unit}})',
    duration: 'Durée (minutes)',
    durationHint: 'Ajoute un bouton minuteur à cette étape.',
    notes: 'Notes',
    addNotes: 'Ajouter des notes',
    notesPlaceholder: 'Astuces ou précisions pour cette étape',
    stepPhotos: 'Photos de l’étape',
    addStepPhotos: 'Ajouter des photos',
    moveUp: 'Monter l’étape',
    moveDown: 'Descendre l’étape',
    deleteStep: 'Supprimer l’étape',
    dragStep: 'Faire glisser pour réorganiser',
    overview: 'Récapitulatif des ingrédients',
    overviewHint: 'Généré automatiquement à partir de vos étapes « Ajouter des ingrédients ».',
    overviewEmpty: 'Les ingrédients ajoutés dans les étapes apparaissent ici.',
    save: 'Enregistrer la recette',
    saveTitle: 'Enregistrez votre recette',
    saveBody: 'Qui doit pouvoir la voir ?',
    public: 'Publique',
    publicHint: 'Partagez-la avec toute la communauté Dishly.',
    private: 'Privée',
    privateHint: 'Vous seul·e pouvez la voir. Vous pourrez la publier plus tard.',
    recommended: 'Recommandé',
    published: 'Recette publiée !',
    savedPrivate: 'Enregistrée dans vos recettes privées.',
    status: {
      saving: 'Enregistrement…',
      saved: 'Brouillon enregistré',
      local: 'Enregistré sur cet appareil',
      offline: 'Hors ligne : enregistré sur cet appareil',
      error: 'Échec de l’enregistrement',
    },
    fixErrors: 'Veuillez corriger les champs signalés.',
    errors: {
      required: 'Obligatoire',
      number: 'Saisissez un nombre',
      name: 'Donnez un nom à votre recette',
      steps: 'Ajoutez au moins une étape',
      instruction: 'Décrivez cette étape',
      ingredients: 'Ajoutez au moins un ingrédient',
      ingredientName: 'Saisissez l’ingrédient',
    },
    deleteDraft: 'Supprimer le brouillon',
    deleteDraftTitle: 'Supprimer ce brouillon ?',
    deleteDraftBody: 'Cette action est irréversible.',
    restoreTitle: 'Modifications non enregistrées',
    restoreBody: 'Vous avez des modifications du {{date}} qui n’ont pas été enregistrées.',
    aiTitle: 'Rédigée par l’IA',
    aiBody: 'Vérifiez les ingrédients, les quantités et les étapes, puis enregistrez. Rien n’est publié avant.',
    restore: 'Les restaurer',
    discard: 'Ignorer',
    offlineUnavailable: 'Cette recette n’est pas téléchargée. Connectez-vous à Internet pour la modifier.',
    notYours: 'Vous ne pouvez modifier que vos propres recettes.',
  },
  auth: {
    title: 'Bienvenue sur Dishly',
    loginTab: 'Connexion',
    signupTab: 'Inscription',
    email: 'E-mail',
    password: 'Mot de passe',
    displayName: 'Nom affiché',
    displayNameHint: 'Affiché sur vos recettes et vos avis.',
    passwordHint: 'Au moins 8 caractères.',
    showPassword: 'Afficher le mot de passe',
    hidePassword: 'Masquer le mot de passe',
    forgot: 'Mot de passe oublié ?',
    logIn: 'Se connecter',
    createAccount: 'Créer un compte',
    loginToContinue: 'Connectez-vous ou créez un compte pour continuer.',
    welcomeBack: 'Bon retour, {{name}} !',
    checkEmailTitle: 'Vérifiez vos e-mails',
    checkEmailBody: 'Nous avons envoyé un lien à {{email}}. Cliquez dessus pour confirmer votre adresse, puis connectez-vous.',
    resend: 'Renvoyer l’e-mail',
    resent: 'Envoyé ! Vérifiez votre boîte de réception (et les spams).',
    backToLogin: 'Retour à la connexion',
    notVerified: 'Veuillez d’abord confirmer votre adresse e-mail. Le lien se trouve dans votre boîte de réception.',
    recaptcha: 'Protégé par reCAPTCHA : les <privacy>Règles de confidentialité</privacy> et les <terms>Conditions d’utilisation</terms> de Google s’appliquent.',
    verifyTitle: 'Confirmez votre adresse e-mail',
    verifying: 'Confirmation de votre adresse…',
    verified: 'Adresse confirmée !',
    verifiedBody: 'Vous pouvez maintenant vous connecter à Dishly.',
    verifyFailed: 'Impossible de confirmer votre adresse e-mail.',
    verifyExpired: 'Ce lien a expiré. Saisissez votre e-mail pour en recevoir un nouveau.',
    forgotTitle: 'Réinitialiser le mot de passe',
    forgotBody: 'Saisissez votre e-mail et nous vous enverrons un lien pour choisir un nouveau mot de passe.',
    sendLink: 'Envoyer le lien',
    linkSent: 'S’il existe un compte pour {{email}}, un lien de réinitialisation est en route.',
    resetTitle: 'Choisissez un nouveau mot de passe',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmez le nouveau mot de passe',
    passwordsDontMatch: 'Les mots de passe ne correspondent pas.',
    savePassword: 'Enregistrer le mot de passe',
    passwordUpdated: 'Mot de passe mis à jour. Vous pouvez maintenant vous connecter.',
    resetInvalid: 'Ce lien de réinitialisation est invalide ou a expiré.',
    requestNew: 'Demander un nouveau lien',
  },
  account: {
    title: 'Compte',
    profile: 'Profil',
    displayName: 'Nom affiché',
    email: 'E-mail',
    memberSince: 'Membre depuis le {{date}}',
    saved: 'Enregistré',
    preferences: 'Préférences',
    units: 'Unités préférées',
    metric: 'Métriques (g, ml, °C)',
    imperial: 'Impériales (oz, tasses, °F)',
    language: 'Langue par défaut',
    theme: 'Thème',
    themeSystem: 'Système',
    themeLight: 'Clair',
    themeDark: 'Sombre',
    security: 'Mot de passe',
    resetPassword: 'Réinitialiser le mot de passe',
    resetHint: 'Nous vous enverrons un lien pour choisir un nouveau mot de passe.',
    resetSent: 'Le lien de réinitialisation vous a été envoyé par e-mail.',
    logOut: 'Se déconnecter',
  },
  timers: {
    title: 'Minuteurs',
    done: 'Terminé !',
    pause: 'Mettre en pause',
    resume: 'Reprendre',
    cancel: 'Annuler le minuteur',
    dismiss: 'Fermer',
    started: 'Minuteur lancé : {{duration}}',
    notificationTitle: 'Minuteur terminé',
    goToStep: 'Aller à l’étape',
  },
  pwa: {
    updateAvailable: 'Une nouvelle version de Dishly est disponible.',
    update: 'Mettre à jour',
  },
  notFound: {
    title: 'Page introuvable',
    body: 'Nous n’avons pas trouvé ce que vous cherchiez.',
    home: 'Voir les recettes',
  },
  errors: {
    generic: 'Une erreur s’est produite. Veuillez réessayer.',
    network: 'Vous êtes hors ligne ou le serveur est injoignable.',
    codes: {
      invalid_credentials: 'L’e-mail ou le mot de passe est incorrect.',
      email_not_verified: 'Veuillez d’abord confirmer votre adresse e-mail.',
      rate_limited: 'Trop de tentatives. Patientez quelques minutes puis réessayez.',
      captcha_required: 'Veuillez valider le captcha.',
      captcha_failed: 'La vérification captcha a échoué. Veuillez réessayer.',
      captcha_unavailable: 'Impossible de vérifier le captcha. Veuillez réessayer.',
      token_expired: 'Ce lien a expiré.',
      token_invalid: 'Ce lien n’est pas valide ou a déjà été utilisé.',
      email_failed: 'Impossible d’envoyer l’e-mail. Réessayez dans un instant.',
      too_large: 'C’est trop volumineux.',
      unsupported_image: 'Envoyez une image JPEG, PNG, WebP, GIF ou AVIF.',
      invalid_image: 'Cette image n’a pas pu être traitée.',
      too_many_lists: 'Vous avez atteint le nombre maximal de listes.',
      own_recipe: 'Vous ne pouvez pas noter votre propre recette.',
      not_found: 'Introuvable.',
      forbidden: 'Vous n’avez pas l’autorisation de faire cela.',
      unauthorized: 'Veuillez vous connecter pour continuer.',
      validation_error: 'Certains champs ne sont pas valides.',
      invalid_recipe: 'Veuillez corriger les champs signalés.',
      builtin_list: 'Les listes par défaut ne peuvent pas être modifiées.',
      server_error: 'Une erreur s’est produite de notre côté.',
      ai_disabled: 'Les fonctions d’IA ne sont pas disponibles pour le moment.',
      ai_unavailable: 'Le service d’IA ne répond pas. Réessayez dans un instant.',
      ai_failed: 'L’IA n’a pas pu terminer. Veuillez réessayer.',
      ai_refused: 'L’IA ne peut pas répondre à cette demande.',
      unsupported_audio: 'Ce format d’enregistrement n’est pas pris en charge.',
      invalid_url: 'Cela ne ressemble pas à une adresse web.',
      page_unreachable: 'Impossible d’ouvrir cette page. Vérifiez le lien et réessayez.',
      page_blocked: 'Ce site ne permet pas aux applications de lire ses recettes. Faites une capture d’écran et utilisez « Photographier une recette papier ».',
      not_a_page: 'Ce lien n’est pas une page web.',
    },
  },
  time: {
    seconds_one: '{{count}} s',
    seconds_other: '{{count}} s',
    minutes_one: '{{count}} min',
    minutes_other: '{{count}} min',
    hours_one: '{{count}} h',
    hours_other: '{{count}} h',
    hoursMinutes: '{{hours}} h {{minutes}} min',
  },
  units: {
    g: 'g',
    kg: 'kg',
    oz: 'oz',
    lb: 'lb',
    ml: 'ml',
    l: 'l',
    tsp: 'c. à c.',
    tbsp: 'c. à s.',
    cup_one: 'tasse',
    cup_other: 'tasses',
    fl_oz: 'fl oz',
    pint_one: 'pinte',
    pint_other: 'pintes',
    pinch_one: 'pincée',
    pinch_other: 'pincées',
    clove_one: 'gousse',
    clove_other: 'gousses',
    slice_one: 'tranche',
    slice_other: 'tranches',
    can_one: 'boîte',
    can_other: 'boîtes',
    bunch_one: 'botte',
    bunch_other: 'bottes',
    sprig_one: 'brin',
    sprig_other: 'brins',
    handful_one: 'poignée',
    handful_other: 'poignées',
    package_one: 'paquet',
    package_other: 'paquets',
  },
  unitNames: {
    g: 'grammes (g)',
    kg: 'kilogrammes (kg)',
    oz: 'onces (oz)',
    lb: 'livres (lb)',
    ml: 'millilitres (ml)',
    l: 'litres (l)',
    tsp: 'cuillères à café (c. à c.)',
    tbsp: 'cuillères à soupe (c. à s.)',
    cup: 'tasses',
    fl_oz: 'onces liquides (fl oz)',
    pint: 'pintes',
    pinch: 'pincée',
    clove: 'gousses',
    slice: 'tranches',
    can: 'boîtes',
    bunch: 'bottes',
    sprig: 'brins',
    handful: 'poignées',
    package: 'paquets',
  },
  unitGroups: {
    mass: 'Poids',
    volume: 'Volume',
    other: 'Autres',
  },
};

export default fr;
